// Visual toasts (§11b). Spoken text goes through announce(); toasts never own a live region.
// Success dismisses after 4 s; errors stay ≥ 8 s, and until dismissed when they carry an action.
// Hover or focus pauses dismissal.
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';
import { announce } from '../lib/announce';
import { cn } from '../lib/cn';

export type ToastKind = 'success' | 'error' | 'info';
export type ToastAction = { label: string; onClick: () => void };
export type ToastItem = { id: number; kind: ToastKind; text: string; action?: ToastAction };

let seq = 0;
let items: ToastItem[] = [];
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export const TOAST_MS = { success: 4000, info: 4000, error: 8000 } as const;

export function toast(text: string, kind: ToastKind = 'success', opts: { action?: ToastAction; silent?: boolean } = {}): number {
  const id = ++seq;
  items = [...items.slice(-3), { id, kind, text, action: opts.action }];
  emit();
  if (!opts.silent) announce(text, kind === 'error' ? 'assertive' : 'polite');
  return id;
}
export const dismissToast = (id: number) => {
  items = items.filter((t) => t.id !== id);
  emit();
};

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

function ToastView({ t }: { t: ToastItem }) {
  const paused = useRef(false);
  const remaining = useRef<number>(TOAST_MS[t.kind]);
  const sticky = t.kind === 'error' && !!t.action;

  useEffect(() => {
    if (sticky) return;
    let last = Date.now();
    const iv = setInterval(() => {
      const now = Date.now();
      if (!paused.current) remaining.current -= now - last;
      last = now;
      if (remaining.current <= 0) dismissToast(t.id);
    }, 250);
    return () => clearInterval(iv);
  }, [t.id, sticky]);

  const Icon = t.kind === 'error' ? AlertCircle : t.kind === 'success' ? CheckCircle2 : Info;
  return (
    <div
      onMouseEnter={() => (paused.current = true)}
      onMouseLeave={() => (paused.current = false)}
      onFocus={() => (paused.current = true)}
      onBlur={() => (paused.current = false)}
      className={cn(
        'wd-toast pointer-events-auto flex w-full items-start gap-3 rounded-xl border bg-card p-3 pl-4 text-sm text-ink shadow-[0_10px_30px_rgb(20_22_27/0.14)]',
        t.kind === 'error' ? 'border-danger-border' : 'border-border',
      )}
    >
      <Icon aria-hidden className={cn('mt-0.5 size-5 shrink-0', t.kind === 'error' ? 'text-danger' : t.kind === 'success' ? 'text-success' : 'text-info')} />
      <p className="min-w-0 flex-1 py-0.5">{t.text}</p>
      {t.action && (
        <button
          type="button"
          onClick={() => {
            t.action!.onClick();
            dismissToast(t.id);
          }}
          className="h-8 shrink-0 rounded-md px-2 font-semibold text-primary-text hover:bg-primary-soft"
        >
          {t.action.label}
        </button>
      )}
      <button type="button" aria-label="Dismiss" onClick={() => dismissToast(t.id)} className="-m-1 inline-flex size-8 shrink-0 items-center justify-center rounded-md text-muted hover:bg-surface">
        <X aria-hidden className="size-4" />
      </button>
    </div>
  );
}

/** Rendered once in the shell, above the bottom tab bar on phones. */
export function ToastViewport() {
  const list = useSyncExternalStore(subscribe, () => items, () => items);
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-[calc(4.5rem+env(safe-area-inset-bottom))] z-50 flex flex-col items-center gap-2 px-3 lg:right-4 lg:bottom-4 lg:left-auto lg:w-96 lg:items-end">
      {list.map((t) => (
        <ToastView key={t.id} t={t} />
      ))}
    </div>
  );
}
